
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import Navigate from "../../components/Navigate";
import { isFormComplete } from "../../util/util";
import { notifyUser, looksLikePhoneNumber, normalizeGhanaPhone, validateGhanaPhone } from "../../util/util";
import { useLogin } from "../../hooks/useAuth";
import { ReactComponent as LockIcon } from '../../assets/UiElements/lock.svg';
import openInNewIcon from '../../assets/UiElements/open_in_new.png';

export const Login = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email_or_phone: "", password: "" });
  const [passwordType, setPasswordType] = useState("password");
  const [marginTop, setMarginTop] = useState('0');
  const [mounted, setMounted] = useState(false);
  const loginMutation = useLogin();

  useEffect(() => {
    const updateMarginTop = () => {
      setMarginTop(window.innerWidth <= 690 ? '50px' : '0');
    };
    updateMarginTop();
    window.addEventListener('resize', updateMarginTop);
    return () => window.removeEventListener('resize', updateMarginTop);
  }, []);

  // Delay rendering of omk-text-field until after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Removed unused togglePasswordType function
  const togglePasswordType = () => {
    setPasswordType(passwordType === "password" ? "text" : "password");
  };
  const changeHandler = (e) => {
    const { name, value } = e.target;
    // No phoneError state needed
    setForm({ ...form, [name]: value });
  };
  const isButtonDisabled = false;

  const submitLogin = async () => {
    let value = form.email_or_phone.trim();
    
    // Check if input looks like a phone number
    const isPhoneInput = looksLikePhoneNumber(value);
    
    // Create a copy of form to modify for submission
    let submissionForm = { ...form };
    
    if (isPhoneInput) {
      // Normalize and validate phone number
      const normalized = normalizeGhanaPhone(value);
      const validation = validateGhanaPhone(normalized);
      
      if (!validation.isValid) {
        notifyUser('error', validation.error, 'Validation Error');
        return;
      }
      
      // Update the submission form with normalized phone number
      submissionForm.email_or_phone = normalized;
      value = normalized;
    }
    
    if (isFormComplete(form)) {
      if (/^[^a-zA-Z0-9]/.test(form.password)) {
        notifyUser('error', 'Password must not start with a special character', 'Validation Error');
        return;
      }
      // Real login flow
      const isPhone = isPhoneInput;
      const payload = isPhone
        ? { ...submissionForm, phone: value }
        : submissionForm;
      loginMutation.mutate(payload, {
        onSuccess: (response) => {
          // Check if phone verification is required
          const phoneVerified = response?.data?.data?.phone_verified;
          const maskedPhone = response?.data?.data?.phone; // API returns masked phone in "phone" field
          const otpRef = response?.data?.data?.otp_ref; // OTP reference for verification
          
          if (phoneVerified === "false" || phoneVerified === false) {
            // Phone not verified - redirect to post-login OTP page
            // Note: Token is NOT returned yet - it will be returned after OTP verification
            navigate('/onboard/verify-phone-otp', { 
              state: { 
                otpRef,        // For OTP verification (pass with otp)
                maskedPhone    // Display only (from API response e.g., "233***75")
              } 
            });
          } else {
            // Phone verified - proceed to dashboard
            navigate('/dashboard');
          }
        },
        onError: (error) => {
          // Phone-not-verified is handled in onSuccess (HTTP 200 with phone_verified: false + otp_ref)
          // Invalid credentials: error notification already shown by useLogin hook; stay on login page
        },
      });
    }
  };

  return (
    <>
      {/* Login Card */}
      <div
        className="mx-auto mobile-full-width shadow bg-white p-4 rounded login-width"
        style={{ marginTop, maxWidth: 888, width: '100%', paddingLeft: 16, paddingRight: 16 }}
      >
        <div align="center">
          <p className="lead fw-bold">Login</p>
          <small className="text-center">Sign in into your account</small>

          <div className="mt-3 w-75 mx-auto mobile-full-width">
            <Spin spinning={loginMutation.isPending}>
              {mounted && (
                <omk-text-field
                  label="Email Address or Phone Number"
                  value={form.email_or_phone}
                  type="text"
                  className="mb-3"
                  onInput={changeHandler}
                  name="email_or_phone"
                  placeholder="Enter Email Address or Phone Number (e.g. 05XXXXXXXX)"
                  style={{ borderRadius: '50px', padding: '10px' }}
                />
              )}
              {/* Phone validation errors are now shown as toast notifications */}
                 {mounted && (
              <omk-text-field
                label="Password"
                value={form.password}
                type={passwordType}
                className="mb-4"
                onInput={changeHandler}
                name="password"
                style={{
                  marginBottom: '20px',
                  borderRadius: '50px',
                  padding: '10px',
                }}
                autoComplete="current-password"
              >
                <omk-icon
                  slot="trailing-icon"
                  onClick={togglePasswordType}
                  style={{ cursor: "pointer" }}
                  tabIndex={0}
                  aria-label="Toggle password visibility"
                  role="button"
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') togglePasswordType(); }}
                >
                  <i className={`bi ${passwordType === 'password' ? 'bi-eye-slash' : 'bi-eye'}`} />
                </omk-icon>
              </omk-text-field>
            )}

              <div>
                <Navigate path="/forgot-password/ssp">
                  <p className="text-center omk-text-small fw-bold" style={{ textDecoration: 'underline', marginBottom: '10px' }}>
                    Forgot Password?
                  </p>
                </Navigate>

                <Navigate path="/onboard/verify-ghana-card">
                  <p className="text-center omk-text-small" style={{ marginBottom: '10px' }}>
                    <span style={{ color: '#666' }}>Don’t have an account?</span>
                    <span style={{ fontWeight: 'bold', color: '#38a169' }}> Create One</span>
                  </p>
                </Navigate>

                <p className="text-center omk-text-small text-black-50" style={{ fontSize: '10px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LockIcon style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }} />
                  Your information is confidential and secure
                </p>
              </div>

              <button
                className={`btn ${isButtonDisabled ? 'btn-secondary' : 'btn-primary'} mb-3 w-50 mobile-full-width`}
                onClick={submitLogin}
                disabled={isButtonDisabled}
                style={{ borderRadius: '50px', padding: '10px 20px', height: 'auto' }}
              >
                Login
              </button>
            </Spin>
          </div>
        </div>
      </div>

      {/* Explore other services */}
      <div className="mt-5 mx-auto mobile-full-width  login-width" style={{ maxWidth: 888, width: '100%', paddingLeft: 16, paddingRight: 16 }}>
        <div style={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 20, textAlign: 'center', letterSpacing: 0.2 }}>Explore other services</div>
        <div style={{ width: '100%', margin: '0 auto 12px auto',  }}></div>
<div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))', // flexible columns
    columnGap: '100px',  // horizontal spacing
    rowGap: '15px',     // vertical spacing
    justifyContent: 'center',
    alignItems: 'stretch',
  }}
  className="explore-services-grid"
>
          {[
            { label: 'International Travel Insurance', url: 'https://www.oldmutual.com.gh/personal/international-travel-insurance/' },
            { label: 'Mvest Personal Pensions', url: 'https://www.oldmutual.com.gh/personal/mvest-personal-pension-scheme/' },
            { label: 'Educator Plan', url: 'https://www.oldmutual.com.gh/personal/education-plan/' },
            { label: 'Personal Accident', url: 'https://www.oldmutual.com.gh/personal/personal-accident-policy/' },
            { label: 'Investment Plan', url: 'https://www.oldmutual.com.gh/personal/special-investment-plan/' },
            { label: 'Retirement Salary', url: 'https://www.oldmutual.com.gh/personal/old-mutual-retirement-salary/' },
            { label: 'Special Investment Plan', url: 'https://www.oldmutual.com.gh/personal/special-investment-plan/' },
          ].map((item, idx) => (
            <button
      key={idx}
      type="button"
      className="btn btn-link p-0 d-flex align-items-center explore-link-btn"
      style={{
        color: '#000000ff',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        textDecoration: 'none',
        fontSize: '0.91rem',
        width: '100%',
        whiteSpace: 'nowrap',       // prevent word breaks
                // hide overflow if too long
     
      }}
      onClick={() => window.open(item.url, '_blank')}
    >
              {item.label}
              <img src={openInNewIcon} alt="open in new" style={{ width: 13, height: 13, marginLeft: 7 }} />
            </button>
          ))}
        </div>

        <style>{`
          .explore-link-btn:hover {
            color: #007b3a !important;
            text-decoration: none !important;
          }
          @media (max-width: 767px) { .explore-services-grid { grid-template-columns: 1fr !important; } }
          @media (min-width: 768px) and (max-width: 1199px) { .explore-services-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (min-width: 1200px) { .explore-services-grid { grid-template-columns: repeat(3, 1fr) !important; } }
          /* Make login card breathe on small screens */
          @media (max-width: 767px) {
            .login-width {
              padding-left: 20px !important;
              padding-right: 20px !important;
              margin-left: 12px !important;
              margin-right: 12px !important;
              box-sizing: border-box;
              max-width: calc(100% - 24px) !important;
              border-radius: 12px;
            }
            .mobile-full-width { width: 100% !important; }
            .login-width .mt-3 { width: 100% !important; }
          }
        `}</style>
      </div>
    </>
  );
};
